import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tooltip } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChair } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { Lesson } from "types";

import useModal from "hooks/useModal";
import useCountdown from "hooks/useCountdown";

import BookedClassModal from "common/modal/booking_modal/BookedClassModal";
import ScheduleConflictModal from "common/modal/schedule_conflict_modal";
import BookingModal from "common/modal/booking_modal";
import PastClassModal from "common/modal/past_modal";
import NoBookedGroupModal from "common/modal/booking_modal/NoBookedGroupModal";

// graphql

// styling
import {
  CardContent,
  CardDetails,
  CardDetailsAlignCenter,
  Topic,
  LevelAndChapter,
  CategoryAndCityAndLanguage,
  Language,
  Category,
  City,
  ClassTypeAndSeatsRemaining,
  AvatarContainer,
  InstructorAvatar,
  InstructorName,
} from "./ClassCardStyle";

type ClassCardContentProps = {
  lesson: Lesson;
  section?: string;
  gray?: boolean;
  allowsBooking?: boolean;
};

const ClassCardContent = ({
  lesson,
  section,
  gray,
  allowsBooking,
}: ClassCardContentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("class_card");

  // States for modals
  const [bookedModalVisible, showBooked, closeBooked] = useModal();
  const [scheduleConflictVisible, showConflict, closeConflict] = useModal();
  const [bookingModalVisible, showBooking, closeBooking] = useModal();
  const [pastClassModalVisible, showPast, closePast] = useModal();
  const [noBookedModalVisible, showNoBooked, closeNoBooked] = useModal();

  const timeLeft = useCountdown(lesson.startTime, false); // time left until start time of class

  // HANDLER FOR MOUSE EVENTS
  const handleClickTeacherInfo = () => {
    navigate(`/teacher/${lesson.teacher?.id}`);
  };

  // RENDERING THE COMPONENT
  const renderLevelChapterTopicCategoryCity = () => {
    // Show only topic name if topic name is "Tailor Made Class"
    return (
      <>
        <LevelAndChapter>
          {lesson.level?.title} {lesson.chapter?.name}
        </LevelAndChapter>
        <Topic>{lesson.topic?.title || t("Tailor Made Class")}</Topic>
        <CategoryAndCityAndLanguage>
          {lesson.category && (
            <Category gray={gray}>{lesson.category.toUpperCase()}</Category>
          )}
          {lesson.city && <City gray={gray}>{lesson.city.name}</City>}
          {lesson.language && (
            <Language gray={gray}>{lesson.language.realName}</Language>
          )}
        </CategoryAndCityAndLanguage>
      </>
    );
  };

  const renderClassTypeAndSeatsRemaining = () => {
    // show only class type if class is 1 on 1
    if (lesson.classType === "1 on 1") {
      return (
        <ClassTypeAndSeatsRemaining gray={gray}>
          <span>{lesson.classType} </span>
        </ClassTypeAndSeatsRemaining>
      );
    }
    return (
      <ClassTypeAndSeatsRemaining gray={lesson.seatsRemaining === 0 || gray}>
        <span>{lesson.classType} </span>
        <Tooltip
          placement="bottom"
          title={`${lesson.seatsRemaining} seats remaining`}
          mouseEnterDelay={0.05}
        >
          <span>
            <FontAwesomeIcon icon={faChair as IconProp} />
            <span> {lesson.seatsRemaining}</span>
          </span>
        </Tooltip>
      </ClassTypeAndSeatsRemaining>
    );
  };

  // const EmptyLine = <div style={{ fontSize: 10, marginTop: 5 }}>&nbsp;</div>;

  const handleClickCardDetails = () => {
    if (lesson?.bookedAt) {
      showBooked();
      return;
    }
    if (lesson?.overlapped) {
      showConflict();
      return;
    }

    const isInThePast = moment().isAfter(lesson?.startTime);
    if (isInThePast) {
      showPast();
      return;
    }
    const fourtyEightHoursToSecond = 60 * 60 * 48;
    const isEmptyAndLessThan48Hour =
      timeLeft.totalSecondsLeft < fourtyEightHoursToSecond &&
      lesson.seatsRemaining === lesson.classSize &&
      lesson.classType.toLowerCase() === "small group";
    if (!isInThePast && isEmptyAndLessThan48Hour) {
      showNoBooked();
      return;
    }
    showBooking();
  };

  const renderCardContent = () => {
    // if it's 1-on-1 class in the class search result,
    // only show class type
    if (section === "filter-result" && lesson.classType === "1 on 1") {
      return (
        <CardDetailsAlignCenter
          className="card-details"
          onClick={() => handleClickCardDetails()}
        >
          {renderClassTypeAndSeatsRemaining()}
          <LevelAndChapter style={{ marginBottom: "4px" }}>
            Choose a Topic or <br /> a Tailor Made Class
          </LevelAndChapter>
        </CardDetailsAlignCenter>
      );
    }
    const showTeacherAvatar =
      lesson.teacher?.givenName && lesson.teacher?.familyName;

    const classDetailsPath = !allowsBooking
      ? `/class-details/${lesson.id}`
      : "#";
    // else, return normal card layout
    return (
      <>
        <CardDetails
          className="card-details"
          onClick={() => handleClickCardDetails()}
        >
          <Link to={classDetailsPath}>
            {renderLevelChapterTopicCategoryCity()}
            {renderClassTypeAndSeatsRemaining()}
          </Link>
        </CardDetails>
        {showTeacherAvatar && (
          <AvatarContainer onClick={() => handleClickTeacherInfo()}>
            <InstructorAvatar
              className="instructor-avatar"
              size="large"
              icon={<UserOutlined />}
              src={lesson.teacher?.avatar}
            />
            <InstructorName className="instructor-name">
              {lesson.teacher?.givenName}
            </InstructorName>
            <InstructorName className="instructor-name">
              {lesson.teacher?.familyName}
            </InstructorName>
          </AvatarContainer>
        )}
      </>
    );
  };

  const renderWarning = () => {
    const WarningText = () => {
      if (lesson.rated || lesson.completed)
        return t("You already finished this class");
      if (lesson?.bookedAt) return t("You already booked this class");
      if (lesson?.overlapped)
        return t("You have another booked class for this time slot");

      return <span>&nbsp;</span>;
    };

    if (!gray) return null;

    return (
      <div style={{ textAlign: "center", fontSize: 10, marginTop: 5 }}>
        {WarningText()}
      </div>
    );
  };

  const renderModals = (
    <>
      <BookedClassModal
        visible={bookedModalVisible}
        onCloseModal={() => closeBooked()}
        classType={lesson.classType}
      />
      <ScheduleConflictModal
        visible={scheduleConflictVisible}
        onCloseModal={() => closeConflict()}
      />
      <PastClassModal
        visible={pastClassModalVisible}
        onCloseModal={() => closePast()}
      />
      <NoBookedGroupModal
        visible={noBookedModalVisible}
        onCloseModal={() => closeNoBooked()}
      />

      {allowsBooking && (
        <BookingModal
          visible={bookingModalVisible}
          onCloseModal={() => closeBooking()}
          state={{
            classId: lesson.id,
            classType: lesson.classType,
            filteredLanguageId: lesson?.language?.id,
            classCredit: lesson.credits,
          }}
        />
      )}
    </>
  );

  return (
    <CardContent gray={gray}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {renderCardContent()}
      </div>
      {renderWarning()}
      {renderModals}
    </CardContent>
  );
};

export default ClassCardContent;
